// Inline scrolling PDF viewer with clause highlighting (Phelipe 2026-08-27,
// received contracts): pdf.js renders each page to a canvas in a scrollable
// column, and highlight(page, quote) scrolls to the page and paints the
// text runs where the quoted words live. Built for short received
// contracts, not plan sets — pages render eagerly, capped at 40.
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs"

function workerReady() {
  if (pdfjsLib.GlobalWorkerOptions.workerSrc) return true
  const host = document.querySelector("[data-pdf-worker-url], [data-drawings-specs-pdf-worker-url-value], [data-specifications-pdf-worker-url-value]")
  const url = host && (host.dataset.pdfWorkerUrl || host.dataset.drawingsSpecsPdfWorkerUrlValue || host.dataset.specificationsPdfWorkerUrlValue)
  if (url) pdfjsLib.GlobalWorkerOptions.workerSrc = url
  return !!pdfjsLib.GlobalWorkerOptions.workerSrc
}

const norm = s => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim()

// What the executed PDF stamps (generate_executed_pdf_job): the signature in
// 21pt Allura, date and text boxes in 10pt Helvetica, each times its size.
// The preview is drawn at the same size, so what's on screen is what prints
// (Kloepfer K3-7, 2026-09-24). PAD_* = the dashed box around the text.
const SIG_PT = 21, FIELD_PT = 10, PAD_X = 6, PAD_Y = 2, DESCENT = 0.22

export class PdfInlineViewer {
  constructor(container, url) {
    this.container = container
    this.url = url
    this.pages = []
    // Zoom multiplies the fit-to-width scale; >1 overflows sideways and the
    // container scrolls both ways (Phelipe 2026-08-27).
    this.zoom = 1
    this._lastHl = null
  }

  async load() {
    if (!workerReady()) throw new Error("pdf worker unavailable")
    const buf = await (await fetch(this.url)).arrayBuffer()
    this.doc = await pdfjsLib.getDocument({ data: buf }).promise
    this._fitWidth = Math.max(this.container.clientWidth - 24, 320)
    // Ctrl/Cmd + wheel zooms in place, like every PDF app.
    this.container.addEventListener("wheel", (e) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      this.setZoom(this.zoom * (e.deltaY < 0 ? 1.25 : 0.8))
    }, { passive: false })
    await this._renderAll()
    return this
  }

  async _renderAll() {
    this.container.innerHTML = ""
    this.pages = []
    const count = Math.min(this.doc.numPages, 40)
    for (let i = 1; i <= count; i++) await this._renderPage(i)
  }

  async setZoom(zoom) {
    const next = Math.min(4, Math.max(0.5, zoom))
    if (Math.abs(next - this.zoom) < 0.01 || this._zooming) return
    this._zooming = true
    const ratio = this.container.scrollHeight > 0 ? this.container.scrollTop / this.container.scrollHeight : 0
    this.zoom = next
    try {
      await this._renderAll()
      this.container.scrollTop = ratio * this.container.scrollHeight
      // Repaint the last highlight and the signature marker at the new size.
      if (this._lastHl) await this.highlight(this._lastHl.page, this._lastHl.quote, { scroll: false })
      this._paintMarker()
      this._paintFields()
    } finally {
      this._zooming = false
    }
    this.onZoom?.(this.zoom)
  }

  zoomIn() { return this.setZoom(this.zoom * 1.25) }
  zoomOut() { return this.setZoom(this.zoom * 0.8) }

  // ── Signature placement (Phelipe 2026-08-27) ──────────────────────────
  // While placement is on, clicking a page reports {page, x, y} in PDF
  // user-space points (origin bottom-left — HexaPDF's drawing space) and
  // shows a marker there. The marker survives zoom (stored in PDF coords).
  enablePlacement(onPick) {
    this._onPick = onPick
    this._placeKind = this._placeKind || "signature"
    this.container.style.cursor = "crosshair"
    if (this._placeWired) return
    this._placeWired = true
    this.container.addEventListener("click", (e) => {
      if (!this._onPick) return
      // The click that ends dragging or resizing a box is not a new placement.
      if (this._suppressClick) { this._suppressClick = false; return }
      const wrap = e.target.closest("[data-page]")
      if (!wrap) return
      const num = parseInt(wrap.dataset.page, 10)
      const entry = this.pages[num]
      if (!entry) return
      const rect = wrap.getBoundingClientRect()
      const [pdfX, pdfY] = entry.viewport.convertToPdfPoint(e.clientX - rect.left, e.clientY - rect.top)
      if (this._placeKind === "signature") this.setMarker(num, pdfX, pdfY)
      this._onPick({ page: num, x: pdfX, y: pdfY, kind: this._placeKind })
    })
  }

  disablePlacement() {
    this._onPick = null
    this.container.style.cursor = ""
  }

  setMarker(pageNum, pdfX, pdfY, label) {
    this._marker = { page: pageNum, x: pdfX, y: pdfY, label: label ?? this._marker?.label ?? "Signature",
                     size: this._marker?.size || 1 }
    this._paintMarker()
  }

  // The dashed placement preview becomes a real signature the moment the
  // sign call lands (Phelipe 2026-08-27) — ink, date line, no box.
  markSigned(dateText) {
    // Signed: nothing on the paper moves any more.
    this._locked = true
    this._paintFields()
    if (!this._marker) return
    this._marker.signed = true
    this._marker.dateText = dateText
    this._paintMarker()
  }

  clearMarker() {
    this._marker = null
    this.container.querySelectorAll("[data-sig-marker]").forEach(el => el.remove())
  }

  // ── Date and text fields (Larry K1-36, 2026-09-23) ────────────────────
  // He asked for DocuSign-style placement: not just the signature, but a date
  // and a free-text box, each dropped where he wants it on the paper. Kept in
  // PDF coords like the signature, so they survive zoom.
  // "field" = a click that only reports the spot (the sent-upload page places
  // its own signature / date / text boxes, one per party — no single marker).
  setPlaceKind(kind) { this._placeKind = (kind === "date" || kind === "text" || kind === "field") ? kind : "signature" }

  // Read-only boxes: a contract that's already out for signature still shows
  // where each signature lands, but nothing moves any more.
  setLocked(on) {
    this._locked = !!on
    this._paintFields()
  }

  addField(field) {
    this._fields = this._fields || []
    this._fields.push(field)
    this._paintFields()
    return field
  }

  updateField(id, patch) {
    const f = (this._fields || []).find(x => x.id === id)
    if (f) Object.assign(f, patch)
    this._paintFields()
  }

  removeField(id) {
    this._fields = (this._fields || []).filter(x => x.id !== id)
    this._paintFields()
  }

  fields() { return [...(this._fields || [])] }

  _paintFields() {
    this.container.querySelectorAll("[data-fld-marker]").forEach(el => el.remove())
    for (const f of this._fields || []) {
      const entry = this.pages[f.page]
      if (!entry) continue
      const [x, y] = entry.viewport.convertToViewportPoint(f.x, f.y)
      // A placed signature box (sent uploads, one per party) previews in the
      // stamp's own Allura size; date / name / text boxes in Helvetica.
      const sig = f.kind === "signature"
      const base = (sig ? SIG_PT : FIELD_PT) * entry.viewport.scale, px = base * (f.size || 1)
      const color = f.color || "#0369a1"
      const bg = f.bg || "rgba(240,249,255,.92)"
      const el = document.createElement("div")
      el.dataset.fldMarker = f.id
      const shown = f.label || (f.kind === "date" ? (f.text || "Date") : (f.text || "Text"))
      el.style.cssText = `position:absolute;left:${x - PAD_X}px;top:0;padding:${PAD_Y}px ${PAD_X}px;` +
        `border:1.5px dashed ${color};border-radius:6px;background:${bg};` +
        `color:${color};font-family:${sig ? "'Allura',cursive" : "Helvetica,Arial,sans-serif"};font-size:${px}px;` +
        `line-height:${sig ? 1.15 : 1.2};white-space:nowrap;` +
        (this._locked ? "pointer-events:none;" : "cursor:move;user-select:none;touch-action:none;")
      el.textContent = shown
      entry.wrap.appendChild(el)
      this._seat(el, y, px)
      if (!this._locked) this._makeMovable(el, entry, "field", f.id, base, color)
    }
  }

  // Sit a box so its text's baseline lands on the PDF point, like the stamp.
  _seat(el, y, px) {
    el.style.top = `${y + DESCENT * px + PAD_Y - el.offsetHeight}px`
  }

  // ── Drag and resize (Kloepfer K3-7, 2026-09-24) ───────────────────────
  // "When I place a signature or date wrong I can't move or resize it":
  // before signing, drag a box to move it, drag its corner to resize it.
  // The new spot and size go back through onMarkerChange / onFieldChange.
  _makeMovable(el, entry, kind, id, basePx, color = null) {
    const handle = document.createElement("span")
    handle.title = "Drag to resize"
    handle.style.cssText = "position:absolute;right:-7px;bottom:-7px;width:12px;height:12px;border-radius:3px;" +
      `border:2px solid #fff;background:${color || (kind === "sig" ? "#c2410c" : "#0369a1")};cursor:nwse-resize;touch-action:none;`
    el.appendChild(handle)
    el.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return
      const item = kind === "sig" ? this._marker : (this._fields || []).find(f => f.id === id)
      if (!item) return
      e.preventDefault()
      e.stopPropagation()
      const resizing = e.target === handle
      const s0 = item.size || 1
      const start = { cx: e.clientX, cy: e.clientY, left: el.offsetLeft, top: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight }
      let s = s0, moved = false
      try { el.setPointerCapture(e.pointerId) } catch (_) { /* old browsers: moves still arrive on the box */ }
      const move = (ev) => {
        const dx = ev.clientX - start.cx, dy = ev.clientY - start.cy
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true
        if (resizing) {
          s = Math.min(3, Math.max(0.5, s0 * (start.w + dx) / start.w))
          el.style.fontSize = `${basePx * s}px`
          el.style.top = `${start.top + start.h - el.offsetHeight}px` // the bottom edge stays put
        } else {
          const maxL = entry.wrap.clientWidth - el.offsetWidth, maxT = entry.wrap.clientHeight - el.offsetHeight
          el.style.left = `${Math.min(maxL, Math.max(0, start.left + dx))}px`
          el.style.top = `${Math.min(maxT, Math.max(0, start.top + dy))}px`
        }
      }
      const up = () => {
        el.removeEventListener("pointermove", move)
        el.removeEventListener("pointerup", up)
        el.removeEventListener("pointercancel", up)
        this._suppressClick = true
        setTimeout(() => { this._suppressClick = false }, 0)
        if (!moved) return
        const px = basePx * s
        const ax = el.offsetLeft + PAD_X
        const ay = el.offsetTop + el.offsetHeight - PAD_Y - DESCENT * px
        const [pdfX, pdfY] = entry.viewport.convertToPdfPoint(ax, ay)
        item.x = pdfX
        item.y = pdfY
        item.size = Math.round(s * 100) / 100
        if (kind === "sig") {
          this._paintMarker()
          this.onMarkerChange?.({ page: item.page, x: item.x, y: item.y, size: item.size })
        } else {
          this._paintFields()
          this.onFieldChange?.(id, { x: item.x, y: item.y, size: item.size })
        }
      }
      el.addEventListener("pointermove", move)
      el.addEventListener("pointerup", up)
      el.addEventListener("pointercancel", up)
    })
  }

  _paintMarker() {
    this.container.querySelectorAll("[data-sig-marker]").forEach(el => el.remove())
    const m = this._marker
    if (!m) return
    const entry = this.pages[m.page]
    if (!entry) return
    const [x, y] = entry.viewport.convertToViewportPoint(m.x, m.y)
    const base = SIG_PT * entry.viewport.scale, px = base * (m.size || 1)
    const el = document.createElement("div")
    el.dataset.sigMarker = "1"
    if (m.signed) {
      el.style.cssText = `position:absolute;left:${x}px;top:${y - px * 1.05}px;pointer-events:none;` +
        `color:#1e293b;font-family:'Allura',cursive;font-size:${px}px;line-height:1.1;white-space:nowrap;`
      el.textContent = m.label
      const sub = document.createElement("div")
      sub.style.cssText = `font-family:system-ui,sans-serif;font-size:${Math.max(8, px * 0.31)}px;color:#64748b;`
      sub.textContent = `Signed ${m.dateText || ""} via ConsLog`.trim()
      el.appendChild(sub)
      entry.wrap.appendChild(el)
    } else {
      el.style.cssText = `position:absolute;left:${x - PAD_X}px;top:0;padding:${PAD_Y}px ${PAD_X}px;` +
        "border:1.5px dashed #c2410c;border-radius:6px;background:rgba(255,247,237,.9);" +
        `color:#c2410c;font-family:'Allura',cursive;font-size:${px}px;line-height:1.15;white-space:nowrap;` +
        "cursor:move;user-select:none;touch-action:none;"
      el.textContent = m.label
      entry.wrap.appendChild(el)
      this._seat(el, y, px)
      this._makeMovable(el, entry, "sig", null, base)
    }
  }

  async _renderPage(num) {
    const page = await this.doc.getPage(num)
    const cssWidth = this._fitWidth || Math.max(this.container.clientWidth - 24, 320)
    const base = page.getViewport({ scale: 1 })
    const viewport = page.getViewport({ scale: (cssWidth / base.width) * this.zoom })
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    const wrap = document.createElement("div")
    wrap.dataset.page = num
    wrap.style.cssText = `position:relative;margin:12px auto;width:${Math.floor(viewport.width)}px;` +
      `height:${Math.floor(viewport.height)}px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.12);`
    const canvas = document.createElement("canvas")
    canvas.width = Math.floor(viewport.width * dpr)
    canvas.height = Math.floor(viewport.height * dpr)
    canvas.style.cssText = "width:100%;height:100%;display:block;"
    wrap.appendChild(canvas)
    this.container.appendChild(wrap)

    await page.render({
      canvasContext: canvas.getContext("2d"), viewport,
      transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null
    }).promise
    this.pages[num] = { page, viewport, wrap }
  }

  // Scrolls to the page and paints the quoted words. Returns true when the
  // quote was actually found (the scroll happens regardless).
  async highlight(pageNum, quote, { scroll = true } = {}) {
    const entry = this.pages[pageNum] || this.pages[1]
    if (!entry) return false
    this._lastHl = { page: pageNum, quote }
    this.container.querySelectorAll("[data-hl]").forEach(el => el.remove())
    if (scroll) entry.wrap.scrollIntoView({ behavior: "smooth", block: "start" })

    const target = norm(quote)
    if (!target) return false
    const tc = await entry.page.getTextContent()
    let joined = ""
    const ranges = []
    tc.items.forEach(item => {
      const t = norm(item.str)
      if (!t) return
      const start = joined ? joined.length + 1 : 0
      ranges.push({ start, end: start + t.length, item })
      joined = joined ? `${joined} ${t}` : t
    })

    let idx = joined.indexOf(target)
    let matchLen = target.length
    if (idx < 0) {
      // The model's quote can drift a word — fall back to its first six words.
      const head = target.split(" ").slice(0, 6).join(" ")
      idx = joined.indexOf(head)
      matchLen = head.length
    }
    if (idx < 0) return false

    const hits = ranges.filter(r => r.end > idx && r.start < idx + matchLen)
    hits.forEach(({ item }) => {
      const [x, y] = entry.viewport.convertToViewportPoint(item.transform[4], item.transform[5])
      const w = item.width * entry.viewport.scale
      const h = ((item.height || 9) * entry.viewport.scale) || 12
      const hl = document.createElement("div")
      hl.dataset.hl = "1"
      hl.style.cssText = `position:absolute;left:${x - 1}px;top:${y - h - 1}px;width:${w + 2}px;` +
        `height:${h * 1.3}px;background:rgba(168,85,247,.35);border-radius:2px;pointer-events:none;`
      entry.wrap.appendChild(hl)
    })
    if (hits.length && scroll) {
      entry.wrap.querySelector("[data-hl]")?.scrollIntoView({ behavior: "smooth", block: "center" })
      // Settle from flash to a calm marker so the text stays readable.
      setTimeout(() => this.container.querySelectorAll("[data-hl]")
        .forEach(el => { el.style.background = "rgba(168,85,247,.18)" }), 1600)
    }
    return hits.length > 0
  }
}
